/**
 * Auth Prompt Modal
 * 
 * Prompts anonymous users to sign up when they try to save.
 * Smooth bottom-sheet animation matching Quick Check modal.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  action?: string; // What they're trying to do, e.g. "save this audit"
}

export function AuthPromptModal({ isOpen, onClose, action = 'save this audit' }: AuthPromptModalProps) {
  const navigate = useNavigate();
  const [animating, setAnimating] = useState(false);
  
  // Handle animation
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimating(true);
        });
      });
    }
  }, [isOpen]);
  
  const handleClose = () => {
    setAnimating(false);
    setTimeout(onClose, 200);
  };
  
  const handleSignUp = () => {
    handleClose();
    // Store that they came from save flow
    sessionStorage.setItem('postAuthAction', 'save');
    setTimeout(() => navigate('/signup'), 200);
  };
  
  const handleSignIn = () => {
    handleClose();
    sessionStorage.setItem('postAuthAction', 'save');
    setTimeout(() => navigate('/login'), 200);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end sm:justify-center sm:items-center">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-charcoal-900/50 transition-opacity duration-200 ${
          animating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div
        className={`relative bg-cream-100 w-full sm:max-w-sm sm:rounded-2xl sm:mx-4 rounded-t-2xl overflow-hidden flex flex-col transition-all duration-200 ease-out ${
          animating
            ? 'translate-y-0 sm:translate-y-0 opacity-100 sm:scale-100'
            : 'translate-y-full sm:translate-y-0 opacity-0 sm:scale-95'
        }`}
      >
        {/* Content */}
        <div className="px-6 pt-8 pb-6 text-center">
          {/* Icon */}
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-teal-50 flex items-center justify-center">
            <svg className="w-7 h-7 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
          </div>
          
          <h3 className="text-xl font-bold text-charcoal-800 mb-2">
            Sign in to {action}
          </h3>
          <p className="text-sm text-charcoal-500 leading-relaxed mb-6">
            An account is required to save audits.
          </p>
          
          {/* Sign in link */}
          <p className="text-sm text-charcoal-500">
            Already have an account?{' '}
            <button 
              onClick={handleSignIn}
              className="text-teal-600 font-medium hover:text-teal-700"
            >
              Sign in
            </button>
          </p>
        </div>
        
        {/* Full-width bottom button */}
        <button
          onClick={handleSignUp}
          className="w-full min-h-[80px] pt-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-base font-semibold bg-teal-500 text-white active:bg-teal-600 transition-colors"
        >
          Create Account
        </button>
      </div>
    </div>
  );
}

export default AuthPromptModal;
